import { gql, useMutation } from '@apollo/client'
import { Button, FormControl, Modal } from '@farol-ds/react'
import classNames from 'classnames'
import CPF from 'cpf'
import { useCallback, useRef } from 'react'

import { InputIMask } from '../../components'
import { useValidateOabModalContext } from '../../context'
import styles from '../steps.module.scss'
import {
  EditProfileMutationMutation,
  EditProfileMutationMutationVariables,
  EditProfilePhoneMutationMutation,
  EditProfilePhoneMutationMutationVariables,
} from './__generated__/confirm-id.graphql'

export const EditProfileMutation = gql`
  mutation EditProfileMutation($input: EditProfileInput!) {
    editProfile(input: $input) {
      __typename
    }
  }
`

export const EditProfilePhoneMutation = gql`
  mutation EditProfilePhoneMutation($input: EditProfilePhoneInput!) {
    editProfilePhone(input: $input) {
      __typename
    }
  }
`

export function ConfirmId() {
  const { form, goNextSuccessStep, getServerDataValues } = useValidateOabModalContext()
  const [editProfile] = useMutation<
    EditProfileMutationMutation,
    EditProfileMutationMutationVariables
  >(EditProfileMutation)
  const [editProfilePhone] = useMutation<
    EditProfilePhoneMutationMutation,
    EditProfilePhoneMutationMutationVariables
  >(EditProfilePhoneMutation)

  const {
    register,
    formState: { errors, isValid },
    getFieldState,
  } = form

  const onClick = useCallback(() => {
    const { isDirty: isCpfDirty } = getFieldState('cpf')
    const { isDirty: isBirthDateDirty } = getFieldState('birthDate')
    const { isDirty: isPhoneDirty } = getFieldState('phone')
    const { cpf, birthDate, phoneArea, phoneNumber } = getServerDataValues()

    const profileChanges = {
      ...(isCpfDirty ? { cpf } : {}),
      ...(isBirthDateDirty ? { dataNascimento: birthDate } : {}),
    }

    Promise.all([
      Object.keys(profileChanges).length
        ? editProfile({
            variables: {
              input: {
                changes: profileChanges,
              },
            },
          })
        : Promise.resolve(),
      isPhoneDirty
        ? editProfilePhone({
            variables: {
              input: {
                action: 'ADD',
                area: phoneArea,
                phone: phoneNumber,
              },
            },
          })
        : Promise.resolve(),
    ]).catch((error) => {
      // ignoring errors for now as this is a non-critical step and retried in confirm-oab step
      console.error('Error updating profile:', error)
    })

    goNextSuccessStep()
  }, [editProfile, editProfilePhone, getFieldState, goNextSuccessStep, getServerDataValues])

  return (
    <>
      <Modal.IconClose />
      <Modal.Header>
        <Modal.HeaderTitle>Confirme sua identidade</Modal.HeaderTitle>
        <Modal.HeaderDescription>
          Somente advogados(as) verificados podem acessar peças processuais.
        </Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.cpf,
          })}
          error={!!errors.cpf}
        >
          <FormControl.Label>CPF</FormControl.Label>
          <InputIMask
            {...register('cpf', {
              required: true,
              pattern: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
              validate: (value) => CPF.isValid(value),
            })}
            type="text"
            mask="000.000.000-00"
            lazy={false}
            inputMode="numeric"
          />
          <FormControl.ErrorMessage>Digite um CPF válido</FormControl.ErrorMessage>
        </FormControl>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.birthDate,
          })}
          error={!!errors.birthDate}
        >
          <FormControl.Label>Data de nascimento</FormControl.Label>
          <InputIMask
            {...register('birthDate', {
              required: true,
              pattern: /^\d{2}\/\d{2}\/\d{4}$/,
            })}
            type="text"
            mask={Date}
            pattern="d/`m/`Y"
            format={
              useRef((date: Date) => {
                const day = String(date.getDate()).padStart(2, '0')
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const year = date.getFullYear()
                return [day, month, year].join('/')
              }).current
            }
            parse={
              useRef((str: string) => {
                const [day, month, year] = str.split('/').map(Number)
                return new Date(year, month - 1, day)
              }).current
            }
            lazy={false}
            inputMode="numeric"
          />
          <FormControl.ErrorMessage>Digite uma data válida</FormControl.ErrorMessage>
        </FormControl>
        <FormControl
          className={classNames(styles.formControlMargin, {
            [styles['formControlMargin--error']]: !!errors.phone,
          })}
          error={!!errors.phone}
        >
          <FormControl.Label>Telefone</FormControl.Label>
          <InputIMask
            {...register('phone', { pattern: /^\(\d{2}\) \d{4,5}-\d{4}|\(__\) ____-____$/ })}
            type="text"
            mask={
              useRef([
                { mask: '(00) 0000-0000', lazy: false },
                { mask: '(00) 00000-0000', lazy: false },
              ]).current
            }
            inputMode="numeric"
          />
          <FormControl.ErrorMessage>Digite um telefone válido</FormControl.ErrorMessage>
        </FormControl>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="primary" onClick={onClick} disabled={!isValid}>
          Avançar
        </Button>
      </Modal.Footer>
    </>
  )
}
